import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon, VStack } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegBuilding } from 'react-icons/fa';
import { FiArrowLeft, FiUser } from 'react-icons/fi';
import { AddressRec } from '@netiva/classifieds-api';

import { EmptyAddressData } from '@/pages/Addresses/components';
import { AddressForm, AddressFormVariant } from '@/pages/Addresses/components/AddressForm';
import { useAddress } from '@/pages/Addresses/useAddress';
import { NewAccountHeader } from './NewAccountHeader';

export type NewAccountFormProps = {
  type: AddressFormVariant;
  address?: AddressRec;
  onCancel?: () => void;
  onSaved?: () => void;
};

const defaultValues: AddressRec = { ...EmptyAddressData, type: 'Account' };

export const NewAccountForm: FC<NewAccountFormProps> = ({ type, address, onCancel, onSaved }) => {
  const { t } = useTranslation();
  const { saveAddress, isLoading } = useAddress({ trackingContext: 'homePage' });
  const isCompany = type === 'company';

  const form = useForm<AddressRec>({
    values: address,
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const handleSave = async (data: AddressRec) => {
    // the private form hides the company fields, but an existing address can still carry them
    const addressToSave = isCompany ? data : { ...data, company: null, company2: null, homepageUrl: null };
    if (await saveAddress(addressToSave)) {
      onSaved?.();
    }
  };

  return (
    <FormProvider {...form}>
      <VStack
        as="form"
        onSubmit={form.handleSubmit(handleSave)}
        spacing={8}
        maxWidth="2xl"
        marginX="auto"
        align="stretch"
      >
        {onCancel && (
          <Button
            type="button"
            onClick={onCancel}
            variant="link"
            colorScheme="primary"
            leftIcon={<FiArrowLeft />}
            alignSelf="flex-start"
          >
            {t('new.account.back')}
          </Button>
        )}

        <NewAccountHeader
          icon={<Icon as={isCompany ? FaRegBuilding : FiUser} boxSize={6} />}
          title={isCompany ? t('new.account.profile.company.title') : t('new.account.profile.private.title')}
          description={
            isCompany ? t('new.account.profile.company.description') : t('new.account.profile.private.description')
          }
        />

        <AddressForm variant={type} alignSelf="center" />

        <Flex justify="center">
          <Button type="submit" colorScheme="primary" isLoading={isLoading} isDisabled={isLoading}>
            {t('new.account.finish')}
          </Button>
        </Flex>
      </VStack>
    </FormProvider>
  );
};
